import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Menu, X, ArrowRight, Linkedin, Youtube, ExternalLink } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchCompanies } from "../redux/companiesSlice";
import type { Company } from "../redux/companiesSlice";
import { fetchSocialLinks } from "../redux/socialSlice";
import heroImg   from "../assets/app/foreva.png";
import logoImg   from "../assets/app/logo.png";
import hqImg     from "../assets/app/HQ.png";

/* ─── DESIGN TOKENS ─── */
const C = {
  black:    "#0A0A0A",
  dark:     "#111111",
  card:     "#F8F7F5",
  cardBg:   "#FFFFFF",
  red:      "#F50000",
  redDark:  "#C00000",
  orange:   "#FF4A00",
  amber:    "#FF8A00",
  gold:     "#D88A00",
  white:    "#FFFFFF",
  offWhite: "#F5F3EF",
  gray:     "#888888",
  grayDim:  "#CCCCCC",
  border:   "rgba(0,0,0,0.08)",
  textDark: "#1A1A1A",
  textMid:  "#555555",
};

const HERO_GRADIENT = "linear-gradient(90deg,#0E0A01 0%,#5A3600 20%,#D88A00 40%,#FF8A00 60%,#FF4A00 80%,#F50000 100%)";
const BRAND_GRADIENT = "linear-gradient(90deg,#D88A00,#FF8A00,#FF4A00,#F50000)";
const BRAND_GRADIENT_FULL = "linear-gradient(135deg,#0E0A01 0%,#5A3600 25%,#D88A00 50%,#FF4A00 75%,#F50000 100%)";

/* ─── Font injection ─── */
const fontHref = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

const NAV_LINKS = [
  { label: "About",     path: "https://4reva.com/about-us", external: true },
  { label: "Legal",     path: "https://4reva.com/legal",    external: true },
  { label: "Policy",    path: "https://4reva.com/policy",   external: true },
  { label: "Companies", path: "/companies",                  external: false },
  { label: "Careers",   path: "/careers",                    external: false },
];

/* ════════════════════════════════════════
   DOT-GRID COMPANIES PANEL  (9-dot menu)
   ════════════════════════════════════════ */
function CompaniesGridPanel({
  companies,
  open,
  onClose,
}: {
  companies: Company[];
  open: boolean;
  onClose: () => void;
}) {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed", inset: 0, zIndex: 500,
              background: "rgba(0,0,0,0.35)", backdropFilter: "blur(6px)",
            }}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.88, y: -16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: -16 }}
            transition={{ type: "spring", stiffness: 340, damping: 28 }}
            style={{
              position: "fixed", top: 80, right: 40, zIndex: 600,
              background: C.white,
              borderRadius: 20,
              boxShadow: "0 32px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.06)",
              padding: "28px 24px 24px",
              width: 360,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <p style={{ fontSize: 10, letterSpacing: "0.35em", color: C.orange, fontWeight: 700, textTransform: "uppercase", margin: "0 0 4px", fontFamily: "'Outfit', sans-serif" }}>4REVA GROUP</p>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, margin: 0, color: C.textDark }}>Our Companies</h3>
              </div>
              <button onClick={onClose} style={{ background: "#F0EDEA", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={15} color={C.textDark} />
              </button>
            </div>

            {/* Grid */}
            {companies.length === 0 ? (
              <p style={{ textAlign: "center", color: C.gray, fontSize: 13, padding: "24px 0", fontFamily: "'Outfit', sans-serif" }}>Companies coming soon…</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {companies.map((co, i) => (
                  <CompanyMiniCard key={co.id} company={co} index={i} />
                ))}
              </div>
            )}

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <a href="/companies" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: C.orange, letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none", fontFamily: "'Outfit', sans-serif" }}>
                View all companies <ArrowRight size={13} />
              </a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* Mini card for the 9-dot panel */
function CompanyMiniCard({ company, index }: { company: Company; index: number }) {
  const [hov, setHov] = useState(false);
  const imgSrc = company.logoUrl || company.coverImage;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => company.website && window.open(company.website, "_blank")}
      style={{
        borderRadius: 14,
        padding: "14px 8px 10px",
        textAlign: "center",
        cursor: company.website ? "pointer" : "default",
        background: hov ? "#FFF8F0" : "#FAFAFA",
        transition: "background 0.2s",
        border: hov ? `1.5px solid transparent` : `1.5px solid transparent`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated gradient border on hover */}
      {hov && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: "absolute", inset: 0, borderRadius: 14, zIndex: 0,
            padding: 1.5,
            background: BRAND_GRADIENT,
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }}
        />
      )}

      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, margin: "0 auto 8px",
          background: imgSrc ? "transparent" : BRAND_GRADIENT_FULL,
          overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
        }}>
          {imgSrc ? (
            <img src={imgSrc} alt={company.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: C.white, fontWeight: 600 }}>
              {company.name.slice(0, 2)}
            </span>
          )}
        </div>
        <p style={{ fontSize: 11, fontWeight: 600, color: C.textDark, margin: "0 0 2px", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>{company.name}</p>
        {company.industry && (
          <p style={{ fontSize: 9, color: C.gray, margin: 0, fontFamily: "'Outfit', sans-serif", letterSpacing: "0.05em" }}>{company.industry}</p>
        )}
      </div>
    </motion.div>
  );
}

/* ════════════════════════════════════════
   NINE-DOT BUTTON
   ════════════════════════════════════════ */
function NineDotsButton({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.button
      whileTap={{ scale: 0.9 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        background: hov ? "rgba(255,255,255,0.12)" : "transparent",
        border: "none", cursor: "pointer", borderRadius: "50%",
        width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.2s",
      }}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        {[0,1,2,3,4,5,6,7,8].map(i => {
          const col = i % 3;
          const row = Math.floor(i / 3);
          const cx = 3 + col * 7;
          const cy = 3 + row * 7;
          return (
            <motion.circle
              key={i}
              cx={cx} cy={cy} r="2"
              fill={C.white}
              initial={{ opacity: 0.6 }}
              animate={{ opacity: hov ? 1 : 0.6 }}
              transition={{ delay: hov ? i * 0.03 : 0 }}
            />
          );
        })}
      </svg>
    </motion.button>
  );
}

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
export default function GroupHome() {
  const navigate = useNavigate();
  const [menuOpen,      setMenuOpen]      = useState(false);
  const [gridOpen,      setGridOpen]      = useState(false);
  const [scrolled,      setScrolled]      = useState(false);
  const heroRef  = useRef<HTMLDivElement>(null);
  const { scrollY }  = useScroll();
  const heroY    = useTransform(scrollY, [0, 500], [0, 120]);
  const heroOp   = useTransform(scrollY, [0, 300], [1, 0.25]);

  const dispatch = useDispatch<AppDispatch>();
  const { companies } = useSelector((s: RootState) => s.companies);
  const { social }    = useSelector((s: RootState) => s.social);

  useEffect(() => {
    dispatch(fetchCompanies());
    dispatch(fetchSocialLinks());
  }, [dispatch]);

  const publishedCompanies = useMemo(() => companies.filter(c => c.published), [companies]);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const handleNav = (link: typeof NAV_LINKS[0]) => {
    setMenuOpen(false);
    if (link.external) window.open(link.path, "_blank");
    else if (link.path.startsWith("#")) document.querySelector(link.path)?.scrollIntoView({ behavior: "smooth" });
    else navigate(link.path);
  };

  return (
    <>
      <Helmet>
        <title>4REVA GROUP — Forever Forward</title>
        <meta name="description" content="4REVA GROUP — EU-based technology holding. Building and operating digital platforms across multiple industries. Forever Forward." />
      </Helmet>

      <div style={{ backgroundColor: C.white, minHeight: "100vh", color: C.textDark, fontFamily: "'Outfit', sans-serif", overflowX: "hidden" }}>

        {/* ══ NAV ══ */}
        <motion.nav
          animate={{
            backgroundColor: scrolled ? "rgba(10,10,10,0.96)" : "transparent",
            backdropFilter:   scrolled ? "blur(16px)" : "blur(0px)",
          }}
          transition={{ duration: 0.3 }}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
            padding: "0 40px", height: 72,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
          }}
        >
          {/* Logo */}
          <motion.div whileHover={{ scale: 1.02 }} onClick={() => navigate("/")}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
            <img src={logoImg} alt="4REVA" style={{ height: 36, width: "auto", objectFit: "contain" }} />
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, letterSpacing: "0.22em", color: C.white, fontWeight: 600 }}>
              4REVA GROUP
            </span>
          </motion.div>

          {/* Desktop nav */}
          <div style={{ display: "flex", gap: 32, alignItems: "center" }} className="rg-desktop-nav">
            {NAV_LINKS.map(n => (
              <button key={n.label} onClick={() => handleNav(n)}
                className="rg-nav-link"
                style={{
                  background: "none", border: "none", color: C.white, fontSize: 13,
                  fontWeight: 500, letterSpacing: "0.08em", cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif", opacity: 0.8, transition: "opacity 0.2s",
                  position: "relative", padding: "4px 0",
                }}>
                {n.label}
              </button>
            ))}
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/contact")}
              style={{
                background: BRAND_GRADIENT, color: C.white, border: "none",
                padding: "10px 24px", fontWeight: 600, fontSize: 12,
                letterSpacing: "0.12em", cursor: "pointer",
                fontFamily: "'Outfit', sans-serif", borderRadius: 4,
              }}>
              CONTACT
            </motion.button>

            {/* 9-dot menu */}
            <NineDotsButton onClick={() => setGridOpen(v => !v)} />
          </div>

          {/* Hamburger mobile */}
          <div style={{ display: "none" }} className="rg-hamburger-wrap">
            <NineDotsButton onClick={() => setGridOpen(v => !v)} />
            <button onClick={() => setMenuOpen(true)}
              style={{ background: "none", border: "none", color: C.white, cursor: "pointer", padding: 4 }}>
              <Menu size={24} />
            </button>
          </div>
        </motion.nav>

        {/* ══ COMPANIES GRID PANEL ══ */}
        <CompaniesGridPanel
          companies={publishedCompanies}
          open={gridOpen}
          onClose={() => setGridOpen(false)}
        />

        {/* ══ HERO ══ */}
        <div ref={heroRef} style={{ position: "relative", height: "100vh", overflow: "hidden" }}>
          <motion.div style={{ y: heroY, position: "absolute", inset: 0 }}>
            <img src={heroImg} alt="4REVA GROUP" style={{ width: "100%", height: "115%", objectFit: "cover", objectPosition: "center top" }} />
          </motion.div>

          {/* Hero gradient overlay — matches brand palette */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg,rgba(14,10,1,0.88) 0%,rgba(90,54,0,0.65) 35%,rgba(216,138,0,0.28) 65%,rgba(245,0,0,0.15) 100%)" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "45%", background: "linear-gradient(to bottom, transparent, rgba(14,10,1,0.96))" }} />

          {/* Animated gradient accent line */}
          <div style={{ position: "absolute", top: 0, left: 0, width: 5, height: "100%", background: BRAND_GRADIENT }} />

          <motion.div style={{ opacity: heroOp, position: "absolute", bottom: "11%", left: "6%", maxWidth: 660 }}>
            <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
              style={{ fontSize: 10, letterSpacing: "0.45em", fontWeight: 700, textTransform: "uppercase", marginBottom: 18,
                background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              LONDON, UK · EST. 2026 · REG. 17381555
            </motion.p>

            <motion.h1 initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(48px, 6.5vw, 96px)", fontWeight: 300, lineHeight: 1.03, margin: "0 0 10px", color: C.white }}>
              Forever<br />
              <em style={{ background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic", fontWeight: 400 }}>
                Forward.
              </em>
            </motion.h1>



            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
              style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <motion.button whileTap={{ scale: 0.97 }}
                onClick={() => document.querySelector("#companies")?.scrollIntoView({ behavior: "smooth" })}
                style={{ background: BRAND_GRADIENT, color: C.white, border: "none", padding: "16px 38px", fontWeight: 700, fontSize: 12, letterSpacing: "0.16em", cursor: "pointer", fontFamily: "'Outfit', sans-serif", borderRadius: 4 }}>
                EXPLORE COMPANIES
              </motion.button>
              <motion.button whileHover={{ borderColor: C.amber }} whileTap={{ scale: 0.97 }}
                onClick={() => window.open("https://4reva.com/about-us", "_blank")}
                style={{ background: "transparent", color: C.white, border: "1px solid rgba(255,255,255,0.38)", padding: "16px 38px", fontWeight: 500, fontSize: 12, letterSpacing: "0.16em", cursor: "pointer", fontFamily: "'Outfit', sans-serif", borderRadius: 4, transition: "border-color 0.25s" }}>
                OUR STORY
              </motion.button>





 {/*
<motion.button
  whileTap={{ scale: 0.97 }}
  onClick={() => navigate("/invest")}
  style={{
    background: "transparent",
    color: C.amber,
    border: `1.5px solid ${C.amber}`,
    padding: "16px 38px",
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: "0.16em",
    cursor: "pointer",
    fontFamily: "'Outfit', sans-serif",
    borderRadius: 4,
  }}>
  BECOME AN INVESTOR
</motion.button>

  <motion.button
    whileHover={{ background: C.red, borderColor: C.white }}
    whileTap={{ scale: 0.97 }}
    onClick={() => navigate("/donate")}
    style={{
      background: "transparent",
      color: C.white,
      border: `1.5px solid ${C.red}`,
      padding: "10px 22px",
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: "0.14em",
      cursor: "pointer",
      fontFamily: "'Outfit', sans-serif",
      borderRadius: 4,
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "background 0.2s, border-color 0.2s",
    }}
  >
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z"/>
    </svg>
    DONATE
  </motion.button>
 
 */}

            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.7 }}
            style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.35))" }} />
            <span style={{ fontSize: 9, letterSpacing: "0.35em", color: "rgba(255,255,255,0.35)", textTransform: "uppercase", fontFamily: "'Outfit', sans-serif" }}>Scroll</span>
          </motion.div>
        </div>

        {/* ══ STATS BAR ══ */}
        <div style={{ background: BRAND_GRADIENT, padding: "22px 6%", display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 20 }}>
          {[
            { num: `${publishedCompanies.length || "8"}+`, label: "Companies" },
            { num: "5+",     label: "Countries" },
            { num: "2026",   label: "Founded" },
            { num: "London", label: "Headquartered" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 34, color: C.white, lineHeight: 1, fontWeight: 600 }}>{s.num}</div>
              <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "rgba(255,255,255,0.78)", textTransform: "uppercase", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ══ COMPANIES SECTION ══ */}
        <section id="companies" style={{ padding: "100px 6%", background: C.offWhite }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ marginBottom: 64 }}>
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px",
              background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              Our Portfolio
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 60px)", fontWeight: 400, margin: 0, color: C.textDark, lineHeight: 1.1 }}>
              The Companies
            </h2>
          </motion.div>

          {publishedCompanies.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 20px", color: C.gray, fontSize: 14 }}>
              Check back soon — brands coming shortly.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 }}>
              {publishedCompanies.map((co, i) => (
                <CompanyCard
                  key={co.id}
                  company={co}
                  index={i}
                  onClick={() => co.website ? window.open(co.website, "_blank") : undefined}
                />
              ))}
            </div>
          )}
        </section>



        {/* ══ INVESTOR STRIP ══ */}
<section style={{
  padding: "100px 6%",
  background: C.black,
  backgroundImage: HERO_GRADIENT,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 40,
}}>
  <div>

    <h2 style={{ fontFamily: "'Cormorant Garamond', serif", color: C.white, fontSize: "clamp(28px,3vw,48px)" }}>
      Back the next<br /><em>generation of platforms.</em>
    </h2>
    <p style={{ color: "rgba(255,255,255,0.55)" }}>
      4REVA GROUP is open to strategic investors, angels,
      and institutional partners who share our vision.
    </p>
  </div>
 
</section>

        {/* ══ HQ SECTION ══ */}
        <section style={{ padding: "100px 6%", background: C.white, display: "flex", alignItems: "center", justifyContent: "center", gap: 80, flexWrap: "wrap" }}>
          {/* HQ Image — circular with gradient ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ position: "relative", flexShrink: 0 }}
          >
            {/* Spinning gradient ring */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
              style={{
                position: "absolute", inset: -5, borderRadius: "50%",
                background: `conic-gradient(from 0deg, #D88A00, #FF8A00, #FF4A00, #F50000, #5A3600, #D88A00)`,
                zIndex: 0,
              }}
            />
            {/* Inner white ring */}
            <div style={{ position: "absolute", inset: -1, borderRadius: "50%", background: C.white, zIndex: 1 }} />
            {/* HQ image */}
            <div style={{ position: "relative", zIndex: 2, width: 300, height: 300, borderRadius: "50%", overflow: "hidden", border: `4px solid ${C.white}` }}>
              <img src={hqImg} alt="4REVA GROUP HQ — London, UK" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
            </div>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              style={{
                position: "absolute", bottom: 10, right: -20, zIndex: 3,
                background: C.white, borderRadius: 12, padding: "10px 16px",
                boxShadow: "0 8px 28px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: BRAND_GRADIENT }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: C.textDark, fontFamily: "'Outfit', sans-serif", letterSpacing: "0.04em" }}>London, UK</span>
            </motion.div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ maxWidth: 480 }}
          >
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 18px",
              background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              Our Headquarters
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(28px, 3vw, 48px)", fontWeight: 400, margin: "0 0 22px", color: C.textDark, lineHeight: 1.2 }}>
              Built for the digital era,<br />rooted in London
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { label: "Headquarters", value: "London, United Kingdom" },
                { label: "Incorporated", value: "Estonia, European Union" },
                { label: "Reg. Number",  value: "17381555" },
              ].map(d => (
                <div key={d.label} style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.orange, minWidth: 110, fontFamily: "'Outfit', sans-serif" }}>{d.label}</span>
                  <span style={{ fontSize: 14, color: C.textDark, fontFamily: "'Outfit', sans-serif" }}>{d.value}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ══ ABOUT STRIP ══ */}
        <section style={{ padding: "100px 6%", background: C.dark, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 20px",
              background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              About the Group
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(28px, 3vw, 48px)", fontWeight: 300, margin: "0 0 24px", color: C.white, lineHeight: 1.2 }}>
              A Digital holding 
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.85, color: "rgba(255,255,255,0.58)", margin: "0 0 36px" }}>
  4REVA GROUP is a technology holding company  operating a portfolio of technology brands and services across multiple industries globally. Incorporated in Estonia under EU legal framework.
  
            </p>
            <motion.button whileTap={{ scale: 0.97 }}
              onClick={() => window.open("https://4reva.com/about-us", "_blank")}
              className="rg-grad-btn"
              style={{ background: BRAND_GRADIENT, color: C.white, border: "none", padding: "14px 34px", fontWeight: 700, fontSize: 12, letterSpacing: "0.16em", cursor: "pointer", fontFamily: "'Outfit', sans-serif", borderRadius: 4 }}>
              LEARN MORE
            </motion.button>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {[
              { title: "Innovation", body: "Leveraging technology to solve real-world problems at scale." },
              { title: "Integrity",  body: "EU-regulated, transparent operations across all subsidiaries." },
              { title: "Impact",     body: "Building platforms that matter to millions of people." },
              { title: "Identity",   body: "A strong brand family with distinct purpose and vision." },
            ].map((v, i) => (
              <div key={i} style={{ background: "#181818", padding: "28px 24px", borderTop: i < 2 ? `3px solid transparent` : "none", backgroundClip: "padding-box", position: "relative" }}>
                {i % 2 === 0 && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: BRAND_GRADIENT }} />}
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: C.white, marginBottom: 10, fontWeight: 600 }}>{v.title}</div>
                <div style={{ fontSize: 13, lineHeight: 1.75, color: "rgba(255,255,255,0.48)", fontFamily: "'Outfit', sans-serif" }}>{v.body}</div>
              </div>
            ))}
          </motion.div>
        </section>

        {/* ══ CAREERS STRIP ══ */}
        <section style={{ padding: "80px 6%", background: C.offWhite, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 40, borderTop: `1px solid ${C.border}` }}>
          <div>
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 12px",
              background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
              Join Us
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(26px, 3vw, 42px)", fontWeight: 400, margin: 0, color: C.textDark }}>
              Build the future with us
            </h2>
          </div>
          <motion.button whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/careers")}
            className="rg-outline-grad-btn"
            style={{ background: "transparent", color: C.textDark, padding: "16px 44px", fontWeight: 700, fontSize: 12, letterSpacing: "0.16em", cursor: "pointer", fontFamily: "'Outfit', sans-serif", borderRadius: 4, border: "2px solid transparent", backgroundClip: "padding-box", position: "relative" }}>
            <span style={{ position: "relative", zIndex: 1 }}>VIEW CAREERS</span>
            <div style={{ position: "absolute", inset: -2, borderRadius: 5, background: BRAND_GRADIENT, zIndex: 0 }} />
            <div style={{ position: "absolute", inset: 0, borderRadius: 4, background: C.offWhite, zIndex: 0 }} />
          </motion.button>
        </section>

        {/* ══ FOOTER ══ */}
        <footer style={{ background: "#080808", borderTop: "1px solid rgba(255,255,255,0.06)", padding: "80px 6% 40px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 60, marginBottom: 60 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <img src={logoImg} alt="4REVA" style={{ height: 30, width: "auto", objectFit: "contain" }} />
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, letterSpacing: "0.22em", color: C.white, fontWeight: 600 }}>4REVA GROUP</span>
              </div>
            
             
            </div>

            <div>
              <p style={{ fontSize: 9, letterSpacing: "0.35em", fontWeight: 700, textTransform: "uppercase", marginBottom: 20,
                background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
                Companies
              </p>
              {publishedCompanies.map(co => (
                <div key={co.id} onClick={() => navigate("/companies")} className="rg-footer-link"
                  style={{ fontSize: 13, color: "rgba(255,255,255,0.48)", marginBottom: 12, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                  {co.name}
                </div>
              ))}
            </div>

            <div>
              <p style={{ fontSize: 9, letterSpacing: "0.35em", fontWeight: 700, textTransform: "uppercase", marginBottom: 20,
                background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
                Group
              </p>
              {[
                { label: "About Us", path: "https://4reva.com/about-us", external: true },
                { label: "Careers",  path: "/careers",  external: false },
                { label: "News",     path: "/news",     external: false },
                { label: "Contact",  path: "/contact",  external: false },
              ].map(l => (
                <div key={l.label}
                  onClick={() => l.external ? window.open(l.path, "_blank") : navigate(l.path)}
                  className="rg-footer-link"
                  style={{ fontSize: 13, color: "rgba(255,255,255,0.48)", marginBottom: 12, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                  {l.label}
                </div>
              ))}
            </div>

            <div>
              <p style={{ fontSize: 9, letterSpacing: "0.35em", fontWeight: 700, textTransform: "uppercase", marginBottom: 20,
                background: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Outfit', sans-serif" }}>
                Legal
              </p>
              {[
                { label: "Privacy Policy",   path: "/privacy",    external: false },
                { label: "Terms of Service", path: "/terms",      external: false },
                { label: "Cookie Policy",    path: "/cookies",    external: false },
                { label: "Compliance",       path: "/compliance", external: false },
                { label: "Registration",     path: "https://ariregister.rik.ee/eng/company/17381555/4REVAGROUP-O%C3%9C", external: true },
              ].map(l => (
                <div key={l.label}
                  onClick={() => l.external ? window.open(l.path, "_blank") : navigate(l.path)}
                  className="rg-footer-link"
                  style={{ fontSize: 13, color: "rgba(255,255,255,0.48)", marginBottom: 12, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                  {l.label}
                </div>
              ))}
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.28)", margin: 0, fontFamily: "'Outfit', sans-serif" }}>
© {new Date().getFullYear()} 4REVA GROUP®. All rights reserved. 4REVA GROUP® and all subsidiary brand names are trademarks of 4REVA GROUP.            </p>
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              {social?.instagram && (
                <motion.a whileHover={{ scale: 1.2 }} href={social.instagram} target="_blank" rel="noopener noreferrer"
                  className="rg-social-icon" style={{ color: "rgba(255,255,255,0.38)", display: "flex", transition: "color 0.2s" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
                  </svg>
                </motion.a>
              )}
              {social?.twitter && (
                <motion.a whileHover={{ scale: 1.2 }} href={social.twitter} target="_blank" rel="noopener noreferrer"
                  className="rg-social-icon" style={{ color: "rgba(255,255,255,0.38)", display: "flex", transition: "color 0.2s" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.261 5.632 5.903-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117Z"/>
                  </svg>
                </motion.a>
              )}
              {social?.facebook && (
                <motion.a whileHover={{ scale: 1.2 }} href={social.facebook} target="_blank" rel="noopener noreferrer"
                  className="rg-social-icon" style={{ color: "rgba(255,255,255,0.38)", display: "flex", transition: "color 0.2s" }}>
                  <Linkedin size={18} />
                </motion.a>
              )}
              {social?.youtube && (
                <motion.a whileHover={{ scale: 1.2 }} href={social.youtube} target="_blank" rel="noopener noreferrer"
                  className="rg-social-icon" style={{ color: "rgba(255,255,255,0.38)", display: "flex", transition: "color 0.2s" }}>
                  <Youtube size={18} />
                </motion.a>
              )}
              {!social && (
                <>
                  <div className="rg-social-icon" style={{ color: "rgba(255,255,255,0.38)", display: "flex" }}><Linkedin size={18} /></div>
                  <div className="rg-social-icon" style={{ color: "rgba(255,255,255,0.38)", display: "flex" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.261 5.632 5.903-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117Z"/>
                    </svg>
                  </div>
                  <div className="rg-social-icon" style={{ color: "rgba(255,255,255,0.38)", display: "flex" }}><Youtube size={18} /></div>
                </>
              )}
            </div>
          </div>
        </footer>
      </div>

      {/* ══ MOBILE SIDE MENU ══ */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, backdropFilter: "blur(6px)" }} />
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.3 }}
              style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: "82%", maxWidth: 340, background: C.dark, zIndex: 400, padding: "32px", display: "flex", flexDirection: "column", borderRight: "1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 48 }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, letterSpacing: "0.2em", color: C.white, fontWeight: 600 }}>4REVA GROUP</span>
                <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", color: C.white, cursor: "pointer" }}>
                  <X size={22} />
                </button>
              </div>
              {NAV_LINKS.map((n, i) => (
                <motion.div key={n.label}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
                  onClick={() => handleNav(n)}
                  style={{ fontSize: 24, fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: C.white, marginBottom: 28, cursor: "pointer", paddingBottom: 28, borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {n.label}
                  <ArrowRight size={15} color={C.orange} />
                </motion.div>
              ))}
              <motion.button whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
                onClick={() => { setMenuOpen(false); navigate("/contact"); }}
                style={{ background: BRAND_GRADIENT, color: C.white, border: "none", padding: "16px 0", fontWeight: 700, fontSize: 12, letterSpacing: "0.18em", cursor: "pointer", fontFamily: "'Outfit', sans-serif", borderRadius: 4, marginTop: "auto" }}>
                CONTACT US
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        /* Nav link gradient hover */
        .rg-nav-link {
          position: relative;
        }
        .rg-nav-link::after {
          content: '';
          position: absolute;
          bottom: -2px; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg,#D88A00,#FF8A00,#FF4A00,#F50000);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.25s ease;
          border-radius: 2px;
        }
        .rg-nav-link:hover { opacity: 1 !important; }
        .rg-nav-link:hover::after { transform: scaleX(1); }

        /* Footer links */
        .rg-footer-link { transition: color 0.2s; }
        .rg-footer-link:hover {
          background: linear-gradient(90deg,#D88A00,#FF8A00,#FF4A00,#F50000);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* Social icon hover */
        .rg-social-icon:hover {
          color: #FF8A00 !important;
        }

        /* Responsive */
        @media (max-width: 960px) {
          .rg-desktop-nav { display: none !important; }
          .rg-hamburger-wrap { display: flex !important; gap: 8px; align-items: center; }
        }
        @media (max-width: 860px) {
          section[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; gap: 48px !important; }
          footer > div:first-child { grid-template-columns: 1fr 1fr !important; gap: 40px !important; }
        }
        @media (max-width: 600px) {
          footer > div:first-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

/* ════════════════════════════════════════
   COMPANY CARD — Airbnb-style rounded
   ════════════════════════════════════════ */
function CompanyCard({ company, index, onClick }: {
  company: Company;
  index: number;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const imgSrc = company.coverImage || company.logoUrl;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07, duration: 0.5 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        borderRadius: 20,
        overflow: "hidden",
        cursor: "pointer",
        background: C.cardBg,
        boxShadow: hovered
          ? "0 20px 48px rgba(0,0,0,0.14)"
          : "0 4px 20px rgba(0,0,0,0.07)",
        transition: "box-shadow 0.35s ease, transform 0.35s ease",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        position: "relative",
      }}
    >
      {/* Animated gradient border on hover */}
      <motion.div
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        style={{
          position: "absolute", inset: 0, borderRadius: 20, zIndex: 10,
          padding: 2,
          background: BRAND_GRADIENT,
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
        }}
      />

      {/* Cover image */}
      <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden" }}>
        {imgSrc ? (
          <motion.img
            src={imgSrc}
            alt={company.name}
            animate={{ scale: hovered ? 1.07 : 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: BRAND_GRADIENT_FULL, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 48, color: "rgba(255,255,255,0.35)", fontWeight: 300 }}>
              {company.name.slice(0, 2)}
            </span>
          </div>
        )}

        {/* Industry tag */}
        {company.industry && (
          <div style={{ position: "absolute", top: 12, left: 12, background: "rgba(255,255,255,0.95)", padding: "4px 12px", fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: C.orange, borderRadius: 100, fontFamily: "'Outfit', sans-serif" }}>
            {company.industry}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: "18px 20px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: C.textDark, margin: "0 0 4px", fontWeight: 600, letterSpacing: "0.02em" }}>{company.name}</p>
            <p style={{ fontSize: 12, color: C.textMid, margin: 0, fontFamily: "'Outfit', sans-serif", lineHeight: 1.5 }}>
              {company.tagline || company.description?.slice(0, 58)}
            </p>
          </div>
          <motion.div
            animate={{ opacity: hovered ? 1 : 0, x: hovered ? 0 : 6 }}
            transition={{ duration: 0.3 }}
            style={{ marginLeft: 12, flexShrink: 0 }}
          >
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: BRAND_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ExternalLink size={13} color={C.white} />
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}